import { LOGGER_INTERVAL } from "../constants";
import { ENVIRONMENT, loggerConfig } from "../env";
import { GRANT_TYPES } from "../functions/OAuth";
import { Batch, LogLine, LogLevel, FetchOperation } from "../types/Logger";
import { getPlatform } from "../functions/platform";
import { ApiErrorT } from "../types/Common";
import { isDev } from "../functions/permissions";
import { Capacitor } from "@capacitor/core";

class Logger {
  public constructor(url: string, job: string) {
    this.url = url;
    this.job = job;
    this.environment = ENVIRONMENT;
    this.platform = Capacitor.getPlatform();
  }

  public async run() {
    if (this.url === "" || this.job === "") {
      return;
    }

    this.runLoop = true;
    while (this.runLoop) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await this.sendBatch();
        this.interval = LOGGER_INTERVAL;
      } catch {
        this.interval = LOGGER_INTERVAL * 4;
      }
      // eslint-disable-next-line no-await-in-loop
      await this.wait(this.interval);
    }
  }

  public stop() {
    this.runLoop = false;
    try {
      this.sendBatch();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }

  public error(logLine: LogLine) {
    this.log(LogLevel.error, logLine);
  }

  public warn(logLine: LogLine) {
    this.log(LogLevel.warn, logLine);
  }

  public info(logLine: LogLine) {
    this.log(LogLevel.info, logLine);
  }

  public debug(logLine: LogLine) {
    this.log(LogLevel.debug, logLine);
  }

  private log(level: LogLevel, logLine: LogLine) {
    if (this.url === "" || this.job === "") {
      return;
    }

    const stringifiedLogLine = JSON.stringify(logLine);

    const levelIndex = this.batch.findIndex((b) => b.level === level);

    if (levelIndex === -1) {
      this.batch.push({
        level,
        logs: [[`${Date.now() * 1000 * 1000}`, `${stringifiedLogLine}`]],
      });
    } else {
      this.batch[levelIndex].logs.push([
        `${Date.now() * 1000 * 1000}`,
        `${stringifiedLogLine}`,
      ]);
    }
  }

  private wait(duration: number) {
    return new Promise<void>((resolve) => {
      setTimeout(resolve, duration);
    });
  }

  private clearBatch() {
    this.batch = [];
  }

  private sendBatch() {
    return new Promise<void>((resolve, reject) => {
      if (this.batch.length === 0) {
        resolve();
      } else {
        try {
          Promise.all(
            this.batch.map(({ level, logs }) =>
              fetch(this.url, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  streams: [
                    {
                      stream: {
                        job: this.job,
                        level,
                        environment: this.environment,
                        platform: this.platform,
                      },
                      values: logs,
                    },
                  ],
                }),
              })
            )
          );
          this.clearBatch();
          resolve();
        } catch (error) {
          reject(error);
        }
      }
    });
  }

  private url: string;

  private job: string;

  private environment: string;

  private platform: string;

  private runLoop = false;

  private batch: Batch = [];

  private interval = LOGGER_INTERVAL;
}

export const logger = new Logger(
  loggerConfig.loggerURL,
  loggerConfig.loggerJob
);

const getCommonLogLine = (operation: FetchOperation): LogLine => {
  const { method, url, duration, userId } = operation;
  const base: Record<string, string> = {
    clientName: "rust",
    operationName: url ? url.pathname : "",
    operationType: method,
    environment: ENVIRONMENT,
    platform: getPlatform(),
    variables: url ? url.searchParams.toString() : "",
    time: new Date().toISOString(),
  };

  if (duration !== undefined) {
    base.time = String(duration);
  }
  if (userId !== undefined) {
    base.userId = String(userId);
  }

  return base;
};

type GrantType = (typeof GRANT_TYPES)[keyof typeof GRANT_TYPES];

export const getFetchLogLine = (operation: FetchOperation): LogLine => {
  const { method, url, duration } = operation;

  let time: string | undefined;

  if (duration !== undefined) {
    time =
      duration >= 1000
        ? `${(duration / 1000).toFixed(2)} s`
        : `${duration.toFixed()} ms`;
  }

  return {
    message: `${method} request on ${url ? url.pathname : "undefined"}${
      time ? " took " + time : ""
    }`,
    ...getCommonLogLine(operation),
  };
};

export const getTokenLogLine = (
  operation: FetchOperation,
  grantType: GrantType,
  refreshToken?: string
): LogLine => {
  const base: Record<string, string> = {
    ...getFetchLogLine(operation),
    grantType,
  };
  if (refreshToken && isDev()) {
    base.refreshToken = refreshToken;
  }

  return base;
};

export const getFetchErrorLogLine = (
  operation: FetchOperation,
  error: ApiErrorT
): LogLine => {
  return {
    message: error.message,
    ...getCommonLogLine(operation),
    errorStatus: error.status,
  };
};

export const getTokenErrorLogLine = (
  operation: FetchOperation,
  error: ApiErrorT,
  grantType: GrantType,
  refreshToken?: string
): LogLine => {
  const base: Record<string, string> = {
    ...getFetchErrorLogLine(operation, error),
    grantType,
  };
  if (refreshToken && isDev()) {
    base.refreshToken = refreshToken;
  }

  return base;
};
